import { FiberFault } from '../models/fiber-fault';
import { SEVERITY_COLORS } from '../services/chart-theme';

/**
 * Health-view fault helpers, ported from the server-side report generator
 * (VericureDataServices/ReportApiServices.cs: FaultDescriptionTranslations +
 * MapSectionByDescription) so the hybrid Details tab reaches desktop parity:
 *  - fault codes are shown as human-readable messages (English stop-gap until the
 *    multi-lingual work; matches the report + desktop language-file text), and
 *  - faults are routed to the correct CMS(DTS) / Precarriage(Pre) / Main section
 *    by their description code (the backend currently returns the same faults in
 *    all three buckets).
 */

export type FaultSection = 'DTS' | 'Pre' | 'Main';

// 4-state fault severity colours, matching the Desktop's SectionColorConverter
// (viscom_liner Utility/Converters/ColorConverter.cs:150-163 resolving the skin
// resources in Resources/Skins/DarkSkin.xaml / LightSkin.xaml):
//   Ok(0) -> SuccessColor, Warning(1) -> WarningColor, Error(2) -> ErrorColor,
//   FiberBreak(3) -> ForegroundColor4 (grey; the only skin-dependent one).
export const FAULT_SEVERITY_COLORS_DARK: { [ft: string]: string } = {
  '0': SEVERITY_COLORS.ok,
  '1': SEVERITY_COLORS.warning,
  '2': SEVERITY_COLORS.error,
  // FiberBreak is GREY, not red — the manifest calls this out as a trap. It reads
  // as the most severe state and the instinct is to paint it the most alarming
  // colour; the desktop deliberately does not.
  '3': SEVERITY_COLORS.fiberBreak,
};

export const FAULT_SEVERITY_COLORS_LIGHT: { [ft: string]: string } = {
  ...FAULT_SEVERITY_COLORS_DARK,
  '3': '#B4B4B4', // FiberBreak — grey (light skin ForegroundColor4)
};

/**
 * Colour for a fault severity code (FT '0'..'3'). The hybrid UI is dark-themed,
 * so 'dark' is the default; unknown codes fall back to the Ok green (matches the
 * Desktop converter's default branch resolving a foreground resource, but green
 * keeps unknown-severity faults visible without implying an error).
 */
export function faultSeverityColor(
  ft: string,
  theme: 'dark' | 'light' = 'dark'
): string {
  const palette =
    theme === 'light' ? FAULT_SEVERITY_COLORS_LIGHT : FAULT_SEVERITY_COLORS_DARK;
  return palette[ft] ?? palette['0'];
}

/** True for the severities that draw an overlay (Warning/Error/FiberBreak). */
/**
 * Does this severity shade as an ERROR region rather than a warning?
 *
 * FiberBreak groups with Error here. Note the split from the severity DOT
 * palette, where FiberBreak is grey: the manifest gives fault regions only two
 * colours, and a broken fiber is not a warning.
 */
export function isCriticalSeverity(ft: string): boolean {
  return ft === '2' || ft === '3';
}

export function isOverlaySeverity(ft: string): boolean {
  return ft === '1' || ft === '2' || ft === '3';
}

/**
 * Union of a tick's FD/FP/FM buckets, deduped, keeping only real faults
 * (Warning/Error/FiberBreak). The backend serializes the same fault into up to
 * three buckets, so overlays must dedupe before drawing.
 */
export function dedupeRealFaults(
  fd: FiberFault[] | null | undefined,
  fp: FiberFault[] | null | undefined,
  fm: FiberFault[] | null | undefined
): FiberFault[] {
  const seen = new Set<string>();
  const out: FiberFault[] = [];
  for (const f of [...(fd || []), ...(fp || []), ...(fm || [])]) {
    if (!isOverlaySeverity(f.FT)) {
      continue;
    }
    const key = `${f.D}|${f.FT}|${f.PS}|${f.PE}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(f);
  }
  return out;
}

/**
 * A fault's lifetime on the waterfall (Length × Time) chart: the tick range over
 * which the same fault was continuously observed, plus its widest position range.
 */
export interface FaultSpan {
  FT: string;
  D: string;
  /** Widest observed start sample index (loss-trace index). */
  PS: number;
  /** Widest observed end sample index (loss-trace index). */
  PE: number;
  /** First tick (loss row index) the fault was observed at. */
  startTick: number;
  /** Last tick (loss row index) the fault was observed at. */
  endTick: number;
  /** True if the fault was still present in the final row (not yet repaired). */
  open: boolean;
}

/** Sample-index slack when matching a fault across ticks (positions jitter). */
/**
 * Position tolerance (loss-trace samples) for treating a fault in consecutive
 * ticks as the same ongoing span. Exported so FaultSpanAccumulator folds with the
 * identical rule.
 */
export const SPAN_POSITION_TOLERANCE = 5;

/**
 * Derives detection→repair spans from the per-tick FD/FP/FM snapshots.
 *
 * APPROXIMATION (documented by design): the Desktop's waterfall fault boxes come
 * from its FiberStateService fault lifecycle (DetectionTick/RepairTick), which is
 * not on the wire — the API only sends the faults active at each tick. We
 * reconstruct spans by matching faults across consecutive ticks on (FT, D) with
 * overlapping position ranges (± SPAN_POSITION_TOLERANCE samples). Consequences:
 *  - a fault that flickers off for a tick and returns produces two spans;
 *  - two same-type/same-code faults whose ranges drift into overlap can merge;
 *  - recurring faults at the same position render as separate boxes (Desktop
 *    may show one box per lifecycle event — close enough for parity).
 */
export function deriveFaultSpans(
  rows: { FD: FiberFault[]; FP: FiberFault[]; FM: FiberFault[] }[]
): FaultSpan[] {
  const closed: FaultSpan[] = [];
  let open: FaultSpan[] = [];

  for (let t = 0; t < rows.length; t++) {
    const row = rows[t];
    if (!row) {
      continue;
    }
    const faults = dedupeRealFaults(row.FD, row.FP, row.FM);
    const extended = new Set<FaultSpan>();

    for (const f of faults) {
      const match = open.find(
        (s) =>
          !extended.has(s) &&
          s.FT === f.FT &&
          s.D === f.D &&
          s.endTick === t - 1 &&
          s.PS - SPAN_POSITION_TOLERANCE <= f.PE &&
          f.PS <= s.PE + SPAN_POSITION_TOLERANCE
      );
      if (match) {
        match.endTick = t;
        match.PS = Math.min(match.PS, f.PS);
        match.PE = Math.max(match.PE, f.PE);
        extended.add(match);
      } else {
        const span: FaultSpan = {
          FT: f.FT,
          D: f.D,
          PS: f.PS,
          PE: f.PE,
          startTick: t,
          endTick: t,
          open: true,
        };
        open.push(span);
        extended.add(span);
      }
    }

    // Spans not observed this tick are repaired as of this tick.
    const stillOpen: FaultSpan[] = [];
    for (const s of open) {
      if (s.endTick === t || extended.has(s)) {
        stillOpen.push(s);
      } else {
        s.open = false;
        closed.push(s);
      }
    }
    open = stillOpen;
  }

  return [...closed, ...open];
}

/** Fault description code -> human-readable message (English). */
export const FAULT_DESCRIPTION_TRANSLATIONS: { [code: string]: string } = {
  Loss_Error_Check_For_Bends: 'Please check the sensor cable for sharp bends.',
  Loss_Error_Check_Temperatur_Plausibility:
    'The temperature measurement is disturbed. Check the temperature measuring data on plausibility.',
  Loss_Error_Clean_LWL_Plug: 'Please clean the surface of the LWL plug.',
  Loss_Error_Clean_LWL_Plug_Ckeck_Bends:
    'Please clean the surface of the LWL plug and/or check the sensor cable for sharp bends.',
  Loss_Error_Critical_Loss:
    'The temperature measurement is disturbed and cannot be used for the judgement of the thermal curing of the liner.',
  Loss_Error_Fibre_Break: 'A fiber break has been detected.',
  Loss_Error_High_Loss:
    'Please replace the surface of the LWL plug and/or check the sensor cable for sharp bends.',
  Loss_Error_High_Loss_Pre:
    'Please replace the LWL plug and/or check the sensor cable for sharp bends.',
  // Connectivity events (ConnectivityFaultCodes in Viscom_Liner_Project.FiberAnalysis)
  Connectivity_Offline: 'The device lost its connection to the cloud.',
  Connectivity_Online: 'The device reconnected to the cloud.',
  Connectivity_CaughtUp: 'The device finished uploading its buffered data.',
};

/** Human-readable message for a fault description code (falls back to the code). */
export function translateFaultDescription(description: string): string {
  if (!description) {
    return '';
  }
  return FAULT_DESCRIPTION_TRANSLATIONS[description] ?? description;
}

/**
 * Maps a fault description code to its section (matches the WPF LossAlarm section
 * configuration / the report's MapSectionByDescription).
 */
export function mapFaultSection(description: string | null | undefined): FaultSection {
  if (!description) {
    return 'Main';
  }
  // Connectivity events are logged under CMS (DTS).
  if (description.startsWith('Connectivity_')) {
    return 'DTS';
  }
  // DTS (CMS) section codes
  if (description.includes('Clean_LWL_Plug') && !description.includes('Ckeck_Bends')) {
    return 'DTS';
  }
  if (description === 'Loss_Error_High_Loss') {
    return 'DTS';
  }
  // Pre (Precarriage) section codes
  if (description.includes('Clean_LWL_Plug_Ckeck_Bends')) {
    return 'Pre';
  }
  if (description === 'Loss_Error_High_Loss_Pre') {
    return 'Pre';
  }
  // Main section (default): Check_For_Bends, Check_Temperatur_Plausibility,
  // Critical_Loss, Fibre_Break
  return 'Main';
}

/**
 * Re-buckets a tick's faults into the three Details sections by description code.
 * The backend returns the same faults across FD/FP/FM, so we take the union
 * (deduped) and route each fault to its true section. Sections with no real fault
 * fall back to the caller-supplied "OK" default so the desktop's green OK row shows.
 */
export function routeFaultsToSections(
  fd: FiberFault[],
  fp: FiberFault[],
  fm: FiberFault[],
  okDefault: FiberFault[]
): { FD: FiberFault[]; FP: FiberFault[]; FM: FiberFault[] } {
  const seen = new Set<string>();
  const all: FiberFault[] = [];
  for (const f of [...(fd || []), ...(fp || []), ...(fm || [])]) {
    // Ignore the OK placeholder faults; they are re-added per-section below.
    // Connectivity Online/CaughtUp are OK-severity but must still show in the CMS log.
    if (f.FT === '0' && !(f.D && f.D.startsWith('Connectivity_'))) {
      continue;
    }
    const key = `${f.D}|${f.P}|${f.PS}|${f.PE}|${f.V}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    all.push(f);
  }

  // Named for what the classifier returns: mapFaultSection yields 'DTS'.
  //
  // The rendered header stays 'CMS' — that is what viscom_liner displays, and
  // matching the desktop is the goal. So the two names are BOTH correct at their
  // own layer: 'DTS' internally, 'CMS' on screen. Naming the variable after the
  // classifier keeps that boundary explicit rather than looking like a bug.
  const dts = all.filter((f) => mapFaultSection(f.D) === 'DTS');
  const pre = all.filter((f) => mapFaultSection(f.D) === 'Pre');
  const main = all.filter((f) => mapFaultSection(f.D) === 'Main');

  return {
    FD: dts.length ? dts : okDefault,
    FP: pre.length ? pre : okDefault,
    FM: main.length ? main : okDefault,
  };
}

/**
 * Distance, in METRES, within which two reports are the same physical fault.
 *
 * Metres, not sample indices. A previous version used 1 *sample index*, which at
 * SI = 0.1 is a 10 cm window, so one LWL plug reported at 1.80 m and 1.85 m split
 * into two rows. Positions arrive as sample indices, so the comparison converts
 * with SI rather than the tolerance being expressed in the wrong unit.
 */
export const FAULT_POSITION_TOLERANCE_M = 1;

/** Connectivity codes stay visible even at Ok severity — desktop IsConnectivityCode. */
export function isConnectivityCode(description: string | null | undefined): boolean {
  return !!description && description.startsWith('Connectivity_');
}

/** The fabricated all-clear row a section shows when it has nothing to report. */
export function syntheticOkFault(): FiberFault {
  return { D: 'OK', FT: '0', P: 0, SI: 1, V: 0, PS: 0, PE: 0 };
}

/** A fault and the tick window over which it is open. */
interface OpenFault {
  fault: FiberFault;
  detectionTick: number;
  /** Exclusive. Infinity while the fault has never been reported repaired. */
  repairTick: number;
}

/**
 * Which faults are open at the scrubbed tick, per section.
 *
 * THE RULE (viscom, from the desktop source): a fault shows when
 * `detectionTick <= currentTick < repairTick`. Not the latest tick's report, and
 * not an aggregate over the whole cure — both of which this file tried first and
 * both of which were wrong.
 *
 * The distinction matters because the server reports a fault on the ticks where
 * it is DETECTED, not on every tick it remains true. So the per-tick list is a
 * stream of detections, and the open window has to be derived from it: a fault is
 * open from its first detection onward. Nothing on the wire reports a repair —
 * FiberFault carries no tick fields at all — so repairTick stays Infinity, which
 * matches the observed case of an LWL plug open across every tick including the
 * latest. When the API grows a repair signal, that is the single line to change.
 *
 * Reading a stream of detections as "the faults right now" is what made Details
 * read all-OK at tick 549: the fault was detected once, at tick 0.
 */
export class SectionFaultIndex {
  private sections: { [K in FaultSection]: Map<string, OpenFault> } = {
    DTS: new Map(),
    Pre: new Map(),
    Main: new Map(),
  };

  reset(): void {
    this.sections = { DTS: new Map(), Pre: new Map(), Main: new Map() };
  }

  /**
   * Record one tick's detections.
   *
   * `tick` is ABSOLUTE, matching TelemetryRingBuffer addressing, so an open
   * window still lines up with the slider after rows are evicted.
   */
  foldRow(
    tick: number,
    fd: FiberFault[] | null | undefined,
    fp: FiberFault[] | null | undefined,
    fm: FiberFault[] | null | undefined
  ): void {
    for (const f of [...(fd || []), ...(fp || []), ...(fm || [])]) {
      const bucket = this.sections[mapFaultSection(f.D)];
      const key = this.keyFor(f);
      const held = bucket.get(key);
      if (held == null) {
        // First sighting opens the window. Insertion order is the display order,
        // so this is also where a section's row order is decided.
        bucket.set(key, { fault: f, detectionTick: tick, repairTick: Infinity });
      } else if (tick < held.detectionTick) {
        // A backfill can deliver an earlier tick after a later one.
        held.detectionTick = tick;
      }
    }
  }

  /** Fold a whole backfill. Callers reset() first for a new project. */
  rebuildFrom(
    rows: readonly (FaultBearingFaults | undefined)[] | null | undefined,
    firstTick = 0
  ): void {
    let tick = firstTick;
    for (const row of rows || []) {
      if (row) {
        this.foldRow(tick, row.FD, row.FP, row.FM);
      }
      tick++;
    }
  }

  /**
   * The rows to render for one section at one tick.
   *
   * Ok-suppression: an Ok row is hidden once the section holds a real fault —
   * with a carve-out for connectivity codes, which are Ok-severity but are real
   * log entries and stay visible alongside faults.
   *
   * A section ALWAYS returns at least one row. The desktop renders all three
   * headings and fabricates an all-clear row for a section with nothing to
   * report, so an empty array here would hide a section the desktop shows.
   */
  section(
    section: FaultSection,
    currentTick: number,
    scanInProgress = false
  ): FiberFault[] {
    if (scanInProgress) {
      // A scan clears every section. An all-OK Details mid-scan is correct.
      return [syntheticOkFault()];
    }

    const open = [...this.sections[section].values()]
      .filter(
        (o) => o.detectionTick <= currentTick && currentTick < o.repairTick
      )
      .map((o) => o.fault);

    // Every real fault open at this tick, in insertion order — no worst-of, no
    // latest-of. One physical fault is one row because keyFor collapses repeated
    // reports of it, not because a winner is picked.
    const visible = open.filter(
      (f) => f.FT !== '0' || isConnectivityCode(f.D)
    );

    return visible.length > 0 ? visible : [syntheticOkFault()];
  }

  /**
   * Identity of a physical fault: its description and its position, to within
   * FAULT_POSITION_TOLERANCE_M. Position is a sample index, so it is converted to
   * distance before comparing — the unit mix-up this fixes is what split one plug
   * across two rows.
   */
  private keyFor(f: FiberFault): string {
    const metres = f.SI > 0 ? f.P * f.SI : f.P;
    return `${f.D}|${Math.round(metres / FAULT_POSITION_TOLERANCE_M)}`;
  }
}

/** The fields the index reads off a loss row. */
export interface FaultBearingFaults {
  FD: FiberFault[];
  FP: FiberFault[];
  FM: FiberFault[];
}

/** One stop of a schematic segment's gradient. */
export interface SchematicStop {
  offset: number;
  color: string;
}

/**
 * Neutral end colour for the schematic.
 *
 * WHITE, from FiberHealth.xaml: six segments use the literal `White` and only the
 * mould run uses ForegroundColor, which is white in the Dark skin. In the Light
 * skin that one segment fades to BLACK while the others stay white — viscom
 * confirms that is a desktop inconsistency rather than a parity target, so all
 * seven use white here.
 */
export const SCHEMATIC_NEUTRAL = '#FFFFFF';

/**
 * A section's aggregate state — the worst severity currently open in it.
 *
 * This is the whole colour model: a segment is FLAT in its section's state. The
 * earlier attempt coloured by position along the fibre, which looked like the
 * desktop's gradient but was answering a different question. The gradient is a
 * join treatment, not a loss profile.
 */
export function sectionState(faults: readonly FiberFault[]): string {
  let worst = '0';
  for (const f of faults) {
    if (isConnectivityCode(f.D)) {
      continue; // a log entry, not a health state
    }
    if (severityRank(f.FT) > severityRank(worst)) {
      worst = f.FT;
    }
  }
  return worst;
}

/**
 * Severity ordering for "which state represents this section".
 *
 * FiberBreak outranks Error: a broken fiber is strictly worse news than a lossy
 * one. Note it is also the one state whose colour is calmer than the state below
 * it — grey, not red — so ranking and colour deliberately disagree here.
 */
const SECTION_SEVERITY_RANK: { [ft: string]: number } = {
  '0': 0,
  '1': 1,
  '2': 2,
  '3': 3,
};

function severityRank(ft: string): number {
  return SECTION_SEVERITY_RANK[ft] ?? 0;
}

/** SectionColorConverter: Ok green, Warning amber, Error red-orange, FiberBreak grey. */
export function sectionStateColor(ft: string): string {
  return faultSeverityColor(ft);
}
